import { type CSSProperties, type FocusEvent, type MouseEvent, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { Dot } from "@/components/ui/dot";
import {
  OP_CANCEL_LABEL,
  OP_ORIGIN_LABEL,
  OP_STATUS_LABEL,
  OP_STATUS_TONE,
  hmTime,
  latenessCellValue,
  productsLabel,
} from "./production-order-utils";
import type { ProductionOrder } from "../../types/production-orders.types.js";

// Tooltip rica do bloco da timeline (espelha o hover do protótipo). Fundo escuro
// (tokens inverse), conteúdo por status. Renderizada em portal pois o track tem `overflow-x-auto`
// que recortaria um tooltip em fluxo. Posição fixa via CSS var (Tailwind-only).
interface TipPos {
  x: number;
  y: number;
  below: boolean;
}

// HH:mm reusa hmTime (fonte única da feature). fmtDateTime fica local: difere de @/lib/display-labels
// `formatDateTime` (que inclui o ANO) — aqui é dd/MM HH:mm sem ano por compactação do tooltip (D-2).
const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

// Linha de prazo do tooltip. Só aparece quando HÁ prazo (uma OP sem compromisso não
// ganha ruído) e reusa a mesma regra de rótulo da tabela — tooltip e lista nunca divergem.
function dueRow(o: ProductionOrder): string[] {
  if (!o.dueDate) return [];
  const { kind, text } = latenessCellValue(o);
  const suffix =
    kind === "late" ? ` · atraso ${text.replace("+", "")}` : kind === "onTime" ? " · no prazo" : "";
  return [`Prazo: ${fmtDateTime(o.dueDate)}${suffix}`];
}

// Linhas de detalhe específicas do status (progresso/OEE/motivo/responsável). Exportada p/ teste.
export function tipRows(o: ProductionOrder): string[] {
  return [...statusRows(o), ...dueRow(o)];
}

function statusRows(o: ProductionOrder): string[] {
  if (o.status === "RUNNING") {
    const pct =
      o.producedQty != null && o.plannedQty > 0
        ? Math.round((o.producedQty / o.plannedQty) * 100)
        : null;
    const rows = [
      o.producedQty != null
        ? `Progresso: ${o.producedQty}/${o.plannedQty} un${pct != null ? ` · ${pct}%` : ""}`
        : `Planejado: ${o.plannedQty} un`,
    ];
    if (o.startedAt) {
      const delay = Math.round(
        (new Date(o.startedAt).getTime() - new Date(o.plannedStartAt).getTime()) / 60000
      );
      rows.push(`Início real: ${delay >= 0 ? "+" : ""}${delay} min vs planejado`);
    }
    return rows;
  }
  if (o.status === "COMPLETED") {
    return [
      o.oee != null ? `OEE: ${o.oee.toFixed(1)}%` : "OEE: —",
      o.producedQty != null
        ? `Produzido: ${o.producedQty}/${o.plannedQty} un`
        : `Planejado: ${o.plannedQty} un`,
    ];
  }
  if (o.status === "CANCELLED") {
    return [
      `Motivo: ${o.cancelReason ? OP_CANCEL_LABEL[o.cancelReason] : "—"}`,
      o.cancelledAt ? `Cancelada em ${fmtDateTime(o.cancelledAt)}` : "",
    ].filter(Boolean);
  }
  return [`Planejado: ${o.plannedQty} un`, `Responsável: ${o.responsible ?? "—"}`];
}

// Hook de hover/focus: captura a bbox do gatilho e decide se o tooltip abre acima (padrão) ou
// abaixo (quando o bloco está colado ao topo da viewport).
export function useHoverTooltip() {
  const [tip, setTip] = useState<TipPos | null>(null);
  const open = (el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    const below = r.top < 150;
    setTip({ x: r.left + r.width / 2, y: below ? r.bottom + 8 : r.top - 8, below });
  };
  return {
    tip,
    triggerProps: {
      onMouseEnter: (e: MouseEvent<HTMLElement>) => open(e.currentTarget),
      onMouseLeave: () => setTip(null),
      onFocus: (e: FocusEvent<HTMLElement>) => open(e.currentTarget),
      onBlur: () => setTip(null),
    },
  };
}

export function BlockTooltip({ o, tip }: { o: ProductionOrder; tip: TipPos }) {
  return createPortal(
    <div
      role="tooltip"
      style={{ "--x": `${tip.x}px`, "--y": `${tip.y}px` } as CSSProperties}
      className={cn(
        "pointer-events-none fixed left-[var(--x)] top-[var(--y)] z-[70] w-[250px] -translate-x-1/2 rounded-lg bg-bg-inverse p-3 shadow-rd-xl motion-safe:animate-fade-in",
        !tip.below && "-translate-y-full"
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[11px] font-semibold text-text-inverse">
          {o.code ?? "OP"}
        </span>
        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-text-inverse-secondary">
          <Dot tone={OP_STATUS_TONE[o.status]} size={6} halo={false} /> {OP_STATUS_LABEL[o.status]}
        </span>
      </div>
      <p className="mt-1 text-[13px] font-medium text-text-inverse">{productsLabel(o)}</p>
      <p className="text-[11px] text-text-inverse-secondary">
        {o.lineName} · {hmTime(o.plannedStartAt)}–{hmTime(o.expectedEnd)}
        {o.origin === "MANUAL" ? ` · ${OP_ORIGIN_LABEL.MANUAL}` : ""}
      </p>
      <div className="border-border-inverse/30 mt-1.5 space-y-0.5 border-t pt-1.5 text-[11px] text-text-inverse-secondary">
        {tipRows(o).map((r, i) => (
          <p key={i}>{r}</p>
        ))}
      </div>
    </div>,
    document.body
  );
}
